import { TextField, Box, Link } from "@mui/material";
import beautify from "json-beautify";
import { useCallback, useMemo } from "react";

interface Props {
  label: string;
  value: string;
  setValue: (value: string) => void;
  setParsedJson: (value: object) => void;
  setJsonValid: (value: boolean) => void;
  sampleValue: string;
}

const TextSeparator = () => (
  <span
    style={{
      fontFamily: "EMPrintLight",
      paddingLeft: "0.5em",
      paddingRight: "0.5em",
    }}
  >
    |
  </span>
);

const JsonTextArea = ({
  label,
  value,
  setValue,
  setParsedJson,
  setJsonValid,
  sampleValue,
}: Props) => {
  const formatJson = useCallback(
    () => setValue(beautify(JSON.parse(value), null as unknown as [], 2, 80)),
    [value, setValue]
  );

  const parsedJson = useMemo(() => {
    try {
      const parsedValue = JSON.parse(value);
      setParsedJson(parsedValue);
      return parsedValue;
    } catch (error) {
      return false;
    }
  }, [setParsedJson, value]);

  const jsonValid = useMemo(() => {
    const isValid = value !== "" && parsedJson;
    setJsonValid(isValid);
    return isValid;
  }, [value, parsedJson, setJsonValid]);

  const textareaNote = useMemo(() => {
    if (value === "") return "";
    else if (jsonValid) return "Valid JSON";
    return "Invalid JSON";
  }, [value, jsonValid]);

  const loadSample = useCallback(() => {
    setValue(sampleValue);
  }, [sampleValue, setValue]);

  const clearValue = useCallback(() => {
    setValue("");
  }, [setValue]);

  const pasteClipboard = useCallback(() => {
    navigator.clipboard
      .readText()
      .then((clipboardText) => setValue(clipboardText));
  }, [setValue]);

  const handleTextAreaClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.shiftKey) {
        pasteClipboard();
        e.preventDefault();
      }
    },
    [pasteClipboard]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ fontWeight: 500 }}>{label}</Box>

        <Box
          sx={{
            textTransform: "uppercase",
            fontSize: "0.8em",
            display: "flex",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <Link
            component="button"
            onClick={loadSample}
            sx={{ cursor: "pointer" }}
          >
            Load Sample
          </Link>
          <TextSeparator />
          <Link
            component="button"
            onClick={formatJson}
            sx={{ cursor: "pointer" }}
          >
            Format
          </Link>
          <TextSeparator />
          <Link
            component="button"
            onClick={pasteClipboard}
            sx={{ cursor: "pointer" }}
          >
            Paste
          </Link>
          <TextSeparator />
          <Link
            component="button"
            onClick={clearValue}
            sx={{ cursor: "pointer", color: "error.main" }}
          >
            Clear
          </Link>
        </Box>
      </Box>
      <TextField
        fullWidth
        multiline
        rows={20}
        placeholder="Enter JSON here (Shift + Click to paste)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={value !== "" && !jsonValid}
        helperText={textareaNote}
        spellCheck={false}
        onClick={handleTextAreaClick}
        color={jsonValid ? "success" : undefined}
        sx={{
          "& .MuiOutlinedInput-root": {
            fontFamily: "monospace",
          },
        }}
      />
    </Box>
  );
};

export default JsonTextArea;
